import React, { useState, useEffect, useRef, useContext } from 'react';
import { connect } from 'react-redux';
import { Row, Col, Drawer, Form, Input, Space, Button, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import intl from 'react-intl-universal';
import * as BillApi from '@/api/bill';
import {
  togglePreSettleDrawerVisible,
  initPreSettleInfo,
  resetPreSettleInfo,
} from '@/redux/actions/bill/preSettle';
import { parse2Number, parseFen2Yuan, parseYuan2Fen, parseYuan2Currency } from '@/utils/number';

import Payment from '@/components/Payment';
import PayModal from '@/components/Payment/payModal';
import { PayResult, PayModalRef, PayWay } from '@/components/Payment/interface';
import IntlContext from '@/components/Intl/IntlContext';

import { DEPOSIT_SUBJECT_CODE } from '../config';
import { validateAmount, formatAmount } from '../util/helper';
import commonStyles from '../common.module.scss';
import PreSettleInfo from './info';
import styles from './index.module.scss';

function PreSettle(props) {
  const { resvNo, orderMasterInfo, drawerVisible, infoLoading, preSettleInfo } = props;

  const [payForm] = Form.useForm();
  const payModalRef = useRef<PayModalRef>();

  // Payment & PayModal props
  const [initPayTypes, setInitPayTypes] = useState(false);
  const [payType, setPayType] = useState(3);
  const [certNo, setCertNo] = useState('');
  const [wyuid, setWyuid] = useState('');

  // self states
  const [totalAmount, setTotalAmount] = useState(0);
  const [submitLoading, setSubmitLoading] = useState(false);
  const intlCtx = useContext(IntlContext);

  const payResultCallback = (payResult: PayResult) => {
    if (payResult.generatePayOrder) {
      props.togglePreSettleDrawerVisible(false);
      return;
    }
    if (payResult.payed && payResult.result) {
      message.success(intl.get('bill_pre_settle_success'));
      props.togglePreSettleDrawerVisible(false);
      // parent component callback
      if (props.reloadList && typeof props.reloadList === 'function') props.reloadList();
    }

    if (payResult.payed && !payResult.result) {
      message.success(intl.get('bill_pre_settle_fail'));
    }
  };

  const resetPayModalProps = () => {
    setInitPayTypes(false);
    setCertNo('');
  };

  const initInfo = () => {
    props.initPreSettleInfo({
      resvNo,
    });
  };

  const changeVisible = (visible) => {
    if (visible) {
      initInfo();
      setInitPayTypes(true);
    } else {
      // reset states and store
      setTotalAmount(0);
      resetPayModalProps();
      props.resetPreSettleInfo();
    }
  };

  const closeDrawer = () => {
    props.togglePreSettleDrawerVisible(false);
  };

  // 计算应收
  const calcTotalAmount = async () => {
    try {
      const values = await payForm.validateFields();
      const { subjectAmount, depositAmount } = values;
      if (subjectAmount || depositAmount) {
        const total = (+subjectAmount || 0) * 100 + (+depositAmount || 0) * 100;
        setTotalAmount(total / 100);
      } else {
        setTotalAmount(0);
      }
    } catch (err) {
      setTotalAmount(0);
    }
  };

  // 生成预结算单
  const generatePreSettleOrder = (formValue) => {
    const params = {
      fkdFlag: payType === PayWay.Fukuandan,
      ...orderMasterInfo,
      subjectCode: DEPOSIT_SUBJECT_CODE, // 房费预付
      depositAmount: parse2Number(parseYuan2Fen(formValue.depositAmount)),
      subjectAmount: parse2Number(parseYuan2Fen(formValue.subjectAmount)),
      resvNo,
    };
    if (params.roomId === 0) delete params.roomId;
    if (!params.roomNo) delete params.roomNo;

    return BillApi.generateBizOrder(params);
  };

  const submit = async (confirm?: boolean) => {
    let values;
    try {
      values = await payForm.validateFields();
    } catch (err) {
      message.warning(intl.get('bill_pre_settle_input_pay_amount'));
    }

    // 付款单需confirm后下单
    if (!confirm && payType === PayWay.Fukuandan) {
      payModalRef.current.openModal({
        payType,
        showFukuanDanConfirm: true,
      });
      return;
    }

    try {
      setSubmitLoading(true);
      const res = await generatePreSettleOrder(values);

      if (res && res.status) {
        // open payModal
        payModalRef.current.openModal({
          payAmount: parse2Number(parseYuan2Fen(totalAmount)),
          payType,
          payOrderType: 102,
          paymentNo: res.data?.paymentNo,
          certNo,
          wyuid,
          extraParams: {
            trigger: '执行收款，产生付款账目时',
            operationPage: '预结算抽屉',
          },
        });
      } else {
        message.error(res.msg || intl.get('bill_pre_settle_generate_bill_fail'));
      }
      setSubmitLoading(false);
    } catch (err) {
      setSubmitLoading(false);
    }
  };

  useEffect(() => {
    // assign default from values
    const roomLackAmount =
      preSettleInfo.roomLackAmount > 0
        ? parse2Number(parseFen2Yuan(preSettleInfo.roomLackAmount))
        : 0;
    const lackDepositAmount =
      preSettleInfo.lackDepositAmount > 0
        ? parse2Number(parseFen2Yuan(preSettleInfo.lackDepositAmount))
        : 0;

    payForm.setFieldsValue({
      subjectAmount: roomLackAmount,
    });
    payForm.setFieldsValue({
      depositAmount: lackDepositAmount,
    });

    // 汤伟伟计算
    const total = (roomLackAmount * 100 + lackDepositAmount * 100) / 100;

    // set default total amount
    setTotalAmount(total);
  }, [preSettleInfo]);

  useEffect(() => {
    return () => {
      // reset states and store when unmounted
      setTotalAmount(0);
      resetPayModalProps();
      props.resetPreSettleInfo();
    };
  }, []);

  const drawerFooter = () => {
    return (
      <Space size={16} style={{ marginLeft: 8 }}>
        <Button
          type="primary"
          disabled={totalAmount <= 0}
          loading={submitLoading}
          onClick={() => submit()}
        >
          {intl.get('bill_credit_collection')}
        </Button>
        <Button onClick={() => closeDrawer()}>{intl.get('bill_pre_settle_temp_not_pay')}</Button>
      </Space>
    );
  };

  return (
    <Drawer
      width="70%"
      open={drawerVisible}
      title={intl.get('bill_pre_settle_title')}
      maskClosable={false}
      closable={false}
      destroyOnClose
      forceRender
      afterOpenChange={(visible) => changeVisible(visible)}
      extra={
        <Button
          style={{ border: 0 }}
          shape="circle"
          onClick={() => closeDrawer()}
          icon={<CloseOutlined />}
        />
      }
      footer={drawerFooter()}
    >
      <PayModal
        ref={payModalRef}
        payResultCallback={(payResult) => payResultCallback(payResult)}
        onConfirmOk={() => submit(true)}
      />
      <PreSettleInfo info={preSettleInfo} infoLoading={infoLoading} />
      <dl className={styles['bill-payment']}>
        <dt className={commonStyles['bill-block-title']}>{intl.get('bill_credit_cashier')}</dt>
        <dd>
          <Form form={payForm} preserve={false} className={styles['bill-form']}>
            <Row gutter={24}>
              <Col>
                <Form.Item
                  label={intl.get('bill_pre_settle_room_fee_pay')}
                  name="subjectAmount"
                  rules={[
                    {
                      validator: (_, value) => {
                        return validateAmount(value);
                      },
                    },
                  ]}
                >
                  <Input
                    allowClear
                    prefix={intlCtx.TenantBaseConfig.CurrencySymbol}
                    placeholder={intl.get('bill_pre_settle_input_amount')}
                    onBlur={(e) => {
                      const v = formatAmount(e.target.value);
                      payForm.setFieldsValue({
                        subjectAmount: v,
                      });
                      calcTotalAmount();
                    }}
                  />
                </Form.Item>
              </Col>
              <Col>
                <Form.Item
                  label={intl.get('bill_pre_settle_cash_deposit')}
                  name="depositAmount"
                  rules={[
                    {
                      validator: (_, value) => {
                        return validateAmount(value);
                      },
                    },
                  ]}
                >
                  <Input
                    allowClear
                    prefix={intlCtx.TenantBaseConfig.CurrencySymbol}
                    placeholder={intl.get('bill_pre_settle_input_amount')}
                    onBlur={(e) => {
                      const v = formatAmount(e.target.value);
                      payForm.setFieldsValue({
                        depositAmount: v,
                      });
                      calcTotalAmount();
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label={intl.get('bill_credit_account_receivable')} name="totalAmount">
              <span className="amount-pay">{parseYuan2Currency(totalAmount)}</span>
            </Form.Item>
          </Form>
          <Payment
            isInitPayTypes={initPayTypes}
            selectPayType={(type) => setPayType(type)}
            resvNo={resvNo}
            setIdCardNo={(no) => setCertNo(no)}
            setWyuid={(uid) => setWyuid(uid)}
          />
        </dd>
      </dl>
    </Drawer>
  );
}

const mapStateToProps = (state) => {
  const { orderMasterInfo } = state.bill;
  const { drawerVisible, infoLoading, preSettleInfo } = state.preSettle;
  return {
    orderMasterInfo,
    drawerVisible,
    infoLoading,
    preSettleInfo,
  };
};

export default connect(
  mapStateToProps,
  {
    togglePreSettleDrawerVisible,
    initPreSettleInfo,
    resetPreSettleInfo,
  },
  (mapState, mapProps, ownProps) => {
    return {
      ...mapState,
      ...mapProps,
      ...ownProps,
    };
  },
)(PreSettle);
